import { useMemo, useState } from 'react'
import {
  Wallet,
  TrendingUp,
  TrendingDown,
  PieChart,
  ArrowUp,
  ArrowDown,
  Search,
  X,
  Coins,
  Building2,
  Landmark,
} from 'lucide-react'
import { usePortfolio, calculateTotals } from '@/hooks/usePortfolio'
import { formatCurrency, formatNumber, cn } from '@/lib/utils'
import type { Position, ListingType } from '@/types/portfolio'

/**
 * Portfolio Page - displays user's portfolio with positions table.
 *
 * Features:
 * - Portfolio summary cards (cash, value, P&L)
 * - Sortable/filterable positions table
 * - Position type breakdown
 */

type SortField = 'name' | 'type' | 'shares' | 'value' | 'price' | 'avgPrice'
type SortDirection = 'asc' | 'desc'

export function PortfolioPage() {
  const { data: portfolio, isLoading, error } = usePortfolio()
  const [searchTerm, setSearchTerm] = useState('')
  const [sortField, setSortField] = useState<SortField>('value')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [filterType, setFilterType] = useState<ListingType | 'ALL'>('ALL')

  // Calculate totals
  const totals = useMemo(() => {
    if (!portfolio) return null
    return calculateTotals(portfolio)
  }, [portfolio])

  // Filter and sort positions
  const sortedPositions = useMemo(() => {
    if (!portfolio) return []

    let filtered = portfolio.positions

    // Apply type filter
    if (filterType !== 'ALL') {
      filtered = filtered.filter((p) => p.type === filterType)
    }

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      filtered = filtered.filter(
        (p) =>
          p.listing.name.toLowerCase().includes(term) ||
          p.listing.securityIdentifier.toLowerCase().includes(term)
      )
    }

    // Sort
    return [...filtered].sort((a, b) => {
      let aVal: string | number
      let bVal: string | number

      switch (sortField) {
        case 'name':
          aVal = a.listing.name.toLowerCase()
          bVal = b.listing.name.toLowerCase()
          break
        case 'type':
          aVal = a.type
          bVal = b.type
          break
        case 'shares':
          aVal = a.numberOfShares
          bVal = b.numberOfShares
          break
        case 'value':
          aVal = a.volume
          bVal = b.volume
          break
        case 'price':
          aVal = a.lastPrice.value
          bVal = b.lastPrice.value
          break
        case 'avgPrice':
          aVal = a.averageBuyingPrice
          bVal = b.averageBuyingPrice
          break
        default:
          return 0
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1
      return 0
    })
  }, [portfolio, searchTerm, sortField, sortDirection, filterType])

  // Handle sort toggle
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <div className="mb-4 h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <p className="text-text-light">Loading portfolio...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-8 text-center">
        <p className="mb-4 text-danger">Failed to load portfolio</p>
        <button
          onClick={() => window.location.reload()}
          className="rounded-md bg-primary px-4 py-2 text-white hover:bg-primary-dark"
        >
          Retry
        </button>
      </div>
    )
  }

  if (!portfolio || !totals) {
    return null
  }

  return (
    <div className="flex flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white px-4 py-4 sm:px-6">
        <h1 className="text-2xl font-semibold text-text-dark">My Portfolio</h1>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Summary Cards */}
        <div className="grid gap-4 p-4 sm:grid-cols-2 sm:p-6 lg:grid-cols-4">
          <SummaryCard
            title="Total Value"
            value={formatCurrency(totals.totalValue)}
            icon={<Wallet className="h-5 w-5" />}
            color="primary"
          />
          <SummaryCard
            title="Cash"
            value={formatCurrency(totals.cash)}
            subtitle={`Available: ${formatCurrency(totals.uncommittedCash)}`}
            icon={<Coins className="h-5 w-5" />}
            color="success"
          />
          <SummaryCard
            title="Securities"
            value={formatCurrency(totals.bookValue)}
            subtitle={`${portfolio.positions.length} positions`}
            icon={<PieChart className="h-5 w-5" />}
            color="primary"
          />
          <SummaryCard
            title="Stocks"
            value={formatCurrency(totals.stocksVolume)}
            icon={<TrendingUp className="h-5 w-5" />}
            color="success"
          />
        </div>

        {/* Type Breakdown */}
        <div className="grid gap-4 px-4 pb-4 sm:grid-cols-3 sm:px-6">
          <TypeBreakdownCard
            icon={<Building2 className="h-4 w-4" />}
            label="Bonds"
            value={totals.bondsVolume}
          />
          <TypeBreakdownCard
            icon={<Landmark className="h-4 w-4" />}
            label="Repos"
            value={totals.reposVolume}
          />
          <TypeBreakdownCard
            icon={<Coins className="h-4 w-4" />}
            label="Coins"
            value={totals.coinsVolume}
          />
        </div>

        {/* Positions Table */}
        <div className="mx-4 mb-4 rounded-lg border border-gray-200 bg-white sm:mx-6">
          {/* Table Header/Filters */}
          <div className="flex flex-col gap-3 border-b border-gray-200 p-4 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="font-semibold text-text-dark">Positions ({sortedPositions.length})</h2>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              {/* Type Filter */}
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as ListingType | 'ALL')}
                className="rounded-md border border-gray-200 px-3 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="ALL">All Types</option>
                <option value="STOCK">Stocks</option>
                <option value="BOND">Bonds</option>
                <option value="REPO">Repos</option>
                <option value="COIN">Coins</option>
              </select>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search positions..."
                  className="w-full rounded-md border border-gray-200 py-1.5 pl-9 pr-8 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary sm:w-48"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-text"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 text-left text-xs uppercase text-text-muted">
                <tr>
                  <SortableHeader
                    label="Security"
                    field="name"
                    currentField={sortField}
                    direction={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Type"
                    field="type"
                    currentField={sortField}
                    direction={sortDirection}
                    onSort={handleSort}
                    className="hidden sm:table-cell"
                  />
                  <SortableHeader
                    label="Shares"
                    field="shares"
                    currentField={sortField}
                    direction={sortDirection}
                    onSort={handleSort}
                    className="text-right"
                  />
                  <SortableHeader
                    label="Price"
                    field="price"
                    currentField={sortField}
                    direction={sortDirection}
                    onSort={handleSort}
                    className="hidden text-right md:table-cell"
                  />
                  <SortableHeader
                    label="Avg. Cost"
                    field="avgPrice"
                    currentField={sortField}
                    direction={sortDirection}
                    onSort={handleSort}
                    className="hidden text-right lg:table-cell"
                  />
                  <SortableHeader
                    label="Value"
                    field="value"
                    currentField={sortField}
                    direction={sortDirection}
                    onSort={handleSort}
                    className="text-right"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedPositions.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-text-muted">
                      No positions found
                    </td>
                  </tr>
                ) : (
                  sortedPositions.map((position) => (
                    <PositionRow key={position.id} position={position} />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

// Summary Card Component
interface SummaryCardProps {
  title: string
  value: string
  subtitle?: string
  icon: React.ReactNode
  color: 'primary' | 'success' | 'danger'
}

function SummaryCard({ title, value, subtitle, icon, color }: SummaryCardProps) {
  const colorClasses = {
    primary: 'bg-primary-50 text-primary',
    success: 'bg-success-50 text-success',
    danger: 'bg-danger-50 text-danger',
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-center gap-3">
        <div className={cn('rounded-full p-2', colorClasses[color])}>{icon}</div>
        <div>
          <p className="text-sm text-text-muted">{title}</p>
          <p className="text-xl font-semibold text-text-dark">{value}</p>
          {subtitle && <p className="text-xs text-text-muted">{subtitle}</p>}
        </div>
      </div>
    </div>
  )
}

// Type Breakdown Card
interface TypeBreakdownCardProps {
  icon: React.ReactNode
  label: string
  value: number
}

function TypeBreakdownCard({ icon, label, value }: TypeBreakdownCardProps) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3">
      <div className="text-text-muted">{icon}</div>
      <div className="flex-1">
        <p className="text-xs text-text-muted">{label}</p>
        <p className="font-medium">{formatCurrency(value)}</p>
      </div>
    </div>
  )
}

// Sortable Header Component
interface SortableHeaderProps {
  label: string
  field: SortField
  currentField: SortField
  direction: SortDirection
  onSort: (field: SortField) => void
  className?: string
}

function SortableHeader({
  label,
  field,
  currentField,
  direction,
  onSort,
  className,
}: SortableHeaderProps) {
  const isActive = field === currentField

  // Show direction-specific icon when this column is being sorted
  const SortIcon = isActive ? (direction === 'asc' ? ArrowUp : ArrowDown) : ArrowDown

  return (
    <th
      className={cn('cursor-pointer px-4 py-3 hover:bg-gray-100', className)}
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        <span>{label}</span>
        <SortIcon className={cn('h-3 w-3', isActive ? 'text-primary' : 'text-text-muted')} />
      </div>
    </th>
  )
}

// Position Row Component
interface PositionRowProps {
  position: Position
}

function PositionRow({ position }: PositionRowProps) {
  const { listing, numberOfShares, committedShares, volume, lastPrice, averageBuyingPrice } =
    position

  // Calculate P&L
  const pnl = lastPrice.value - averageBuyingPrice
  const pnlPercent = averageBuyingPrice > 0 ? (pnl / averageBuyingPrice) * 100 : 0
  const isProfitable = pnl >= 0

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3">
        <div>
          <a
            href={`/security/${listing.securityIdentifier}`}
            className="font-medium text-text-dark hover:text-primary"
          >
            {listing.name}
          </a>
          <p className="text-xs text-text-muted">{listing.securityIdentifier}</p>
        </div>
      </td>
      <td className="hidden px-4 py-3 sm:table-cell">
        <span
          className={cn(
            'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
            listing.type === 'STOCK' && 'bg-primary-50 text-primary',
            listing.type === 'BOND' && 'bg-blue-50 text-blue-600',
            listing.type === 'REPO' && 'bg-purple-50 text-purple-600',
            listing.type === 'COIN' && 'bg-yellow-50 text-yellow-600'
          )}
        >
          {listing.type}
        </span>
      </td>
      <td className="px-4 py-3 text-right">
        <span>{formatNumber(numberOfShares, 0)}</span>
        {committedShares > 0 && (
          <span className="ml-1 text-xs text-text-muted">
            ({formatNumber(committedShares, 0)} locked)
          </span>
        )}
      </td>
      <td className="hidden px-4 py-3 text-right md:table-cell">
        <div>
          <span>{formatCurrency(lastPrice.value)}</span>
          <span className={cn('ml-2 text-xs', isProfitable ? 'text-success' : 'text-danger')}>
            {isProfitable ? (
              <TrendingUp className="inline h-3 w-3" />
            ) : (
              <TrendingDown className="inline h-3 w-3" />
            )}{' '}
            {pnlPercent.toFixed(1)}%
          </span>
        </div>
      </td>
      <td className="hidden px-4 py-3 text-right lg:table-cell">
        {formatCurrency(averageBuyingPrice)}
      </td>
      <td className="px-4 py-3 text-right font-medium">{formatCurrency(volume)}</td>
    </tr>
  )
}
